import { v4 } from 'uuid';
import { API_BASE_URL, IS_DEV } from '../config/env';
import { apiService } from './api.service';
import { safeAnalytics } from '../utils/analytics';
import { ANALYTICS_EVENTS } from '../constants/analytics';
const uuidv4 = v4;

interface LogContext {
  userId?: string;
  workspaceId?: string;
  correlationId?: string;
  component?: string;
  operation?: string;
  metadata?: Record<string, any>;
}

interface LogEntry {
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  timestamp: string;
  correlationId?: string;
  userId?: string;
  workspaceId?: string;
  component?: string;
  operation?: string;
  error?: string;
  metadata?: Record<string, any>;
}

class HttpStatusError extends Error {
  constructor(readonly status: number) {
    super(`log ingest returned ${status}`);
  }
}

class LoggingService {
  private baseContext: LogContext = {};
  private logQueue: LogEntry[] = [];
  private batchSize = 10;
  private flushInterval = 30000; // 30 seconds
  private maxQueueSize = 100;

  // Retries are BOUNDED. A fetch rejection used to re-queue the batch
  // forever, and on 2026-09-29 that meant a crash report Cloud Armor 403'd
  // at the load balancer (no CORS headers, so the browser reports a network
  // error) was resent every 30 s: 22 identical denials from one tab, visible
  // nowhere. Each entry now gets `maxSendAttempts` sends, spaced at least
  // `flushInterval` apart so an error burst (every error flushes) cannot
  // spend them in a second, and is then dropped loudly. A browser that knows
  // it is offline does not send at all, so genuine network loss spends none.
  private maxSendAttempts = 5;
  private sendAttempts = new WeakMap<LogEntry, number>();
  private nextSendAllowedAt = 0;
  // One send at a time: a burst landing while a send is in flight would
  // otherwise open a request per error before the first failure is known.
  private sending = false;
  // A deadline on each send: `sending` holds every flush while one is in
  // flight, so a stalled connection must not hold it until the OS gives up.
  // An abort rejects the fetch and counts as one spaced attempt.
  private sendTimeoutMs = 15000;

  constructor() {
    // Start periodic flushing in every DEPLOYED environment.
    //
    // This asked `IS_PROD`, which meant "is production" -- fine until a second
    // deployed environment existed. Once staging sets VITE_APP_ENV=staging,
    // IS_PROD is false there, and gating on it would silently stop staging
    // shipping frontend logs to the backend: the exact logs that surfaced a
    // deploy-time error-boundary burst. The question here is "am I deployed?",
    // so ask that.
    if (!IS_DEV) {
      setInterval(() => this.flush(), this.flushInterval);
    }

    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flush());
  }

  setContext(context: Partial<LogContext>) {
    this.baseContext = { ...this.baseContext, ...context };
  }

  clearContext() {
    this.baseContext = {};
  }

  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  error(message: string, error?: Error, context?: LogContext) {
    // The stack stays in `metadata`, NOT on the top-level `error` field.
    //
    // An earlier version of this fix moved it up, on the reasoning that
    // `logs.controller.ts` drops `metadata` and forwards `error`, so the stack
    // never reached production logs. #1545 solved the same problem the other
    // way and better: it forwards an ALLOWLIST out of metadata — `errorName`,
    // `componentStack`, `boundaryLevel`, route — and deliberately refuses
    // `errorMessage`, `errorStack` and `metadata.error`, because an error
    // message is the classic UGC carrier and a V8 stack begins with one.
    // Promoting the stack would have shipped exactly what that decision
    // excludes. The symbolicated message and stack live in PostHog error
    // tracking, which receives the same exception from `ErrorBoundary`.
    this.log('error', message, {
      ...context,
      metadata: {
        ...context?.metadata,
        error: error?.stack || error?.message,
        errorName: error?.name,
      },
    });
  }

  debug(message: string, context?: LogContext) {
    // Only log debug messages in development
    if (IS_DEV) {
      this.log('debug', message, context);
    }
  }

  // Log performance metrics
  performance(operation: string, startTime: number, context?: LogContext) {
    const duration = Date.now() - startTime;
    this.info(`Performance: ${operation}`, {
      ...context,
      metadata: {
        ...context?.metadata,
        duration,
        operation: 'performance',
      },
    });
  }

  // Log user interactions
  userInteraction(action: string, context?: LogContext) {
    this.info(`User Action: ${action}`, {
      ...context,
      operation: 'user_interaction',
      metadata: {
        ...context?.metadata,
        action,
      },
    });
  }

  // Log API calls
  apiCall(method: string, url: string, status: number, duration: number, context?: LogContext) {
    const level = status >= 400 ? 'error' : 'info';
    this.log(level, `API ${method} ${url}`, {
      ...context,
      operation: 'api_call',
      metadata: {
        ...context?.metadata,
        method,
        url,
        status,
        duration,
      },
    });
  }

  private log(level: LogEntry['level'], message: string, context?: LogContext) {
    const logEntry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      correlationId: context?.correlationId || this.baseContext.correlationId,
      userId: context?.userId || this.baseContext.userId,
      workspaceId: context?.workspaceId || this.baseContext.workspaceId,
      component: context?.component || this.baseContext.component,
      operation: context?.operation || this.baseContext.operation,
      metadata: {
        ...this.baseContext.metadata,
        ...context?.metadata,
        userAgent: navigator.userAgent,
        url: window.location.href,
        referrer: document.referrer,
      },
    };

    // Console logging for development
    if (IS_DEV) {
      const warnOrLog = level === 'warn' ? 'warn' : 'log';
      const consoleMethod = level === 'error' ? 'error' : warnOrLog;
      // eslint-disable-next-line no-console
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, logEntry);
    }

    // Queue for backend sending in every DEPLOYED environment (see the
    // constructor: this is "am I deployed?", not "am I production?").
    if (!IS_DEV) {
      this.queueLogEntry(logEntry);
    }
  }

  private queueLogEntry(logEntry: LogEntry) {
    // Add to queue
    this.logQueue.push(logEntry);

    // Prevent memory issues
    if (this.logQueue.length > this.maxQueueSize) {
      this.logQueue = this.logQueue.slice(-this.maxQueueSize);
    }

    // Auto-flush if batch size reached or for errors
    if (this.logQueue.length >= this.batchSize || logEntry.level === 'error') {
      this.flush();
    }
  }

  private async flush() {
    if (this.logQueue.length === 0) return;

    const baseURL = API_BASE_URL;

    // Get auth token from the API service (where it's actually stored)
    // Read BEFORE the queue is drained. The ingest needs a bearer token, and
    // draining first meant a flush attempted while signed out DESTROYED the
    // batch: the no-token return used to sit inside the try, past the drain,
    // and only the catch re-queues. Every log line produced before auth — the
    // whole of /auth and /login, both lazy-loaded routes under the app
    // ErrorBoundary — was thrown away by the very flush an ERROR triggers.
    // Holding them costs nothing: the queue is already capped at
    // `maxQueueSize` and they go out with the first authenticated flush.
    const authToken = apiService.getAuthToken();
    if (!authToken) return;

    if (this.sending || !navigator.onLine || Date.now() < this.nextSendAllowedAt) return;

    const logsToSend = [...this.logQueue];
    this.logQueue = [];
    this.sending = true;
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), this.sendTimeoutMs);

    try {
      const workspaceId = apiService.getWorkspaceId() || localStorage.getItem('workspaceId');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': uuidv4(),
        Authorization: `Bearer ${authToken}`,
      };

      if (workspaceId) {
        headers['x-workspace-id'] = workspaceId;
      }

      // Send to backend logging endpoint
      const response = await fetch(`${baseURL}/logs/frontend`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ logs: logsToSend }),
        signal: controller.signal,
      });

      // `fetch` only rejects on a NETWORK failure, so a 400 from the ingest's
      // Zod validation resolves normally and the catch below never runs: the
      // batch is neither delivered nor re-queued, and up to 100 entries vanish
      // with no trace. That is the failure this whole file exists to prevent.
      if (!response.ok) {
        // A 4xx is deterministic in the payload — re-queueing would replay the
        // same rejected batch forever. Surface it and drop it. A 5xx is
        // transient, so throw and let the catch re-queue.
        if (response.status >= 500) {
          throw new HttpStatusError(response.status);
        }
        // eslint-disable-next-line no-console
        console.error(
          `Log batch rejected by ingest (${response.status}); ${logsToSend.length} entries dropped`,
        );
        safeAnalytics.track(ANALYTICS_EVENTS.FRONTEND_LOGS_DROPPED, {
          reason: 'http_4xx',
          status: response.status,
          dropped_count: logsToSend.length,
        });
      }
    } catch (error) {
      this.nextSendAllowedAt = Date.now() + this.flushInterval;
      this.requeueOrDrop(logsToSend, error);

      // Fallback to console in case of network issues
      console.error('Failed to send logs to backend:', error);

      // Limit retry queue size
      if (this.logQueue.length > this.maxQueueSize) {
        this.logQueue = this.logQueue.slice(-this.maxQueueSize);
      }
    } finally {
      clearTimeout(deadline);
      this.sending = false;
    }
  }

  // Re-queue each entry that still has attempts left; drop the rest loudly.
  private requeueOrDrop(logsToSend: LogEntry[], error: unknown) {
    const retryable: LogEntry[] = [];
    let droppedCount = 0;
    for (const entry of logsToSend) {
      const attempts = (this.sendAttempts.get(entry) ?? 0) + 1;
      if (attempts >= this.maxSendAttempts) {
        droppedCount++;
      } else {
        this.sendAttempts.set(entry, attempts);
        retryable.push(entry);
      }
    }
    this.logQueue.unshift(...retryable);

    if (droppedCount === 0) return;
    // Not routed through this logger: its endpoint is what just failed.
    console.error(
      `Log ingest failed ${this.maxSendAttempts} times; ${droppedCount} entries dropped`,
    );
    const status = error instanceof HttpStatusError ? error.status : undefined;
    safeAnalytics.track(ANALYTICS_EVENTS.FRONTEND_LOGS_DROPPED, {
      reason: status ? 'http_5xx' : 'network',
      ...(status ? { status } : {}),
      dropped_count: droppedCount,
      attempts: this.maxSendAttempts,
    });
  }

  // Manual flush method
  async flushNow() {
    await this.flush();
  }
}

// Create singleton instance
export const logger = new LoggingService();

// Export types for use in components
export type { LogContext };
